import { useState } from "react";
import axios from "axios";
import { CalendarIcon, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { API, apiError, authHeaders, formatDateIt } from "@/lib/api";

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const Label = ({ children }) => <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-medium">{children}</span>;

export const SettingsPanel = ({ config, onSaved }) => {
  const [draft, setDraft] = useState(config);
  const [newLista, setNewLista] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const addLista = () => {
    const v = newLista.trim();
    if (v && !draft.liste.includes(v)) set("liste", [...draft.liste, v]);
    setNewLista("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/config`, draft, { headers: authHeaders() });
      onSaved(data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="glass-strong rounded-[1.5rem] p-6 md:p-8 flex flex-col gap-6" data-testid="admin-settings-panel">
      <h2 className="font-heading italic text-white text-3xl tracking-[-1px]">Evento</h2>
      <div className="flex flex-col gap-2">
        <Label>Data prossimo evento</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="admin-event-date-button" className="glass-input flex items-center gap-3 text-left">
              <CalendarIcon className="w-4 h-4 text-white/60" />
              {formatDateIt(draft.eventDate).long} {draft.eventDate.slice(0, 4)}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-black border-white/10 text-white rounded-2xl" align="start">
            <Calendar mode="single" selected={new Date(`${draft.eventDate}T00:00:00`)}
              defaultMonth={new Date(`${draft.eventDate}T00:00:00`)}
              onSelect={(d) => d && set("eventDate", toIso(d))} data-testid="admin-event-calendar" />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-col gap-2">
        <Label>Liste</Label>
        <div className="flex flex-wrap gap-2" data-testid="admin-liste">
          {draft.liste.map((l) => (
            <span key={l} className="glass rounded-full pl-4 pr-2 py-1.5 text-sm flex items-center gap-2">
              {l}
              <button type="button" aria-label={`Rimuovi ${l}`} data-testid={`admin-remove-lista-${l}`}
                onClick={() => set("liste", draft.liste.filter((x) => x !== l))} className="rounded-full p-1 hover:bg-white/10">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="admin-new-lista-input" className="glass-input" placeholder="Nuova lista" value={newLista}
            onChange={(e) => setNewLista(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLista())} />
          <button type="button" onClick={addLista} data-testid="admin-add-lista-button" className="glass rounded-2xl px-4 hover:bg-white/5"><Plus className="w-4 h-4" /></button>
        </div>
      </div>
      <label className="flex items-center justify-between gap-4 cursor-pointer">
        <span className="flex flex-col">
          <Label>Evento al completo</Label>
          <span className="text-xs text-white/40 mt-1">Mostra "Siamo al Completo" al posto del form</span>
        </span>
        <Switch data-testid="admin-soldout-switch" checked={draft.soldOut} onCheckedChange={(v) => set("soldOut", v)} />
      </label>
      <label className="flex items-center justify-between gap-4 cursor-pointer">
        <span className="flex flex-col">
          <Label>Tema Halloween</Label>
          <span className="text-xs text-white/40 mt-1">Pipistrelli trasparenti vicino alla data in home</span>
        </span>
        <Switch data-testid="admin-halloween-switch" checked={!!draft.halloween} onCheckedChange={(v) => set("halloween", v)} />
      </label>
      <button onClick={save} disabled={saving} data-testid="admin-save-settings"
        className="bg-white text-black rounded-full px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva impostazioni"}
      </button>
    </section>
  );
};
